import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  DynamicFormView,
  DynamicFormConfigurationView,
  EDynamicFormInputType,
  DynamicFormValuesForReactiveForm,
  DynamicFormValues,
  DynamicFormMultipleTypeValues,
} from '../../models/DynamicFormView';

import { FileUploadComponent } from '../upload/file-upload.component';
import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { VendorFormService } from '../../service/vendor.service';

@Component({
  selector: 'app-dynamic-form-embed',
  standalone: true,
  imports: [
    FormsModule,
    FileUploadComponent,
    CommonModule,
    HttpClientModule,
    ReactiveFormsModule,
  ],
  templateUrl: './dynamic-form-embed.component.html',
  styleUrls: ['./dynamic-form-embed.component.css'],
})
export class DynamicFormEmbedComponent implements OnInit, AfterViewInit {
  prefixLabel: string = 'dynamicForm';

  allFormsConfigurations!: DynamicFormView[];
  selectedDynamicForm: any = { DynamicFormView: [] };
  selectedDynamicFormConfigurations!: DynamicFormConfigurationView[];
  dynamicFormValuesForReactiveForm: DynamicFormValuesForReactiveForm[] = [];

  @Input() id!: string;
  @Input() clientId!: string;
  @Input() submitted!: boolean;
  @Input() diableInputFile: boolean = false;
  @Input() sourceId!: number;
  @Input() dynamicFormValues!: DynamicFormValues[];
  @Input() dynamicFormMultipleTypeValue!: DynamicFormMultipleTypeValues;
  @Input() form: any;

  @Output() FormEmbeded: EventEmitter<{
    values: DynamicFormValuesForReactiveForm[];
    dynamicForm: DynamicFormView;
    prefixLabel: string;
  }> = new EventEmitter<{
    values: DynamicFormValuesForReactiveForm[];
    dynamicForm: DynamicFormView;
    prefixLabel: string;
  }>();

  @Output() onDynamicFormFileChange: EventEmitter<{
    file: any;
    propertyName: string;
  }> = new EventEmitter<{
    file: any;
    propertyName: string;
  }>();

  constructor(private vendorFormService: VendorFormService) {}

  ngOnInit(): void {}

  ngAfterViewInit() {
    this.vendorFormService.getAllForm(this.clientId).subscribe(
      (data: DynamicFormView[]) => {
        this.allFormsConfigurations = data;
        this.dynamicFormConfiguration();
        if (this.id && this.dynamicFormMultipleTypeValue) {
          this.dynamicFormValues =
            this.dynamicFormMultipleTypeValue.dynamicFormValues;
          this.onMultipleTypeSelectionChangeWithValue(
            this.dynamicFormMultipleTypeValue
              .dynamicFormMultipleTypeConfigurationId,
          );
        }
      },
      (err: any) => {},
    );
  }

  onFileChange($event: any, propertyName: string) {
    this.onDynamicFormFileChange.emit({
      file: $event,
      propertyName: propertyName,
    });
  }

  dynamicFormConfiguration() {
    let temp: DynamicFormView | undefined;

    if (Array.isArray(this.allFormsConfigurations)) {
      temp = this.allFormsConfigurations.find(
        (x) => x.sourceId === this.sourceId,
      );
    } else if (this.allFormsConfigurations) {
      temp = this.allFormsConfigurations as DynamicFormView;
    }

    if (temp) {
      if (
        temp.dynamicFormConfigurations &&
        Array.isArray(temp.dynamicFormConfigurations)
      ) {
        temp.dynamicFormConfigurations.sort(
          (
            a: DynamicFormConfigurationView,
            b: DynamicFormConfigurationView,
          ) => {
            return a.order - b.order;
          },
        );
      }

      this.selectedDynamicForm = temp;
      this.selectedDynamicFormConfigurations =
        temp.dynamicFormConfigurations || [];

      this.tempLabelConfig(temp.dynamicFormConfigurations, this);
    } else {
      console.warn(
        'No form configuration found for the given sourceId:',
        this.sourceId,
      );
    }
  }

  private tempLabelConfig(
    dynamicFormConfigurations: DynamicFormConfigurationView[],
    context: this,
  ) {
    dynamicFormConfigurations.forEach(
      (config: DynamicFormConfigurationView) => {
        config.tempLabel =
          this.prefixLabel +
          config.label.replace(/[^\w\s]/gi, '').replace(/\s+/g, '');

        let validators = [];

        if (
          config.characterValidation !== null &&
          config.characterValidation > 0
        ) {
          validators.push(Validators.maxLength(config.characterValidation));
        }

        if (
          (config?.isRequired &&
            config?.dynamicFormInputTypeId !=
              EDynamicFormInputType.InformationOnly) ??
          false
        ) {
          validators.push(Validators.required);
        }

        let value = '';
        if (this.id) {
          value =
            context.dynamicFormValues?.find(
              (x) => x.dynamicFormConfigurationId === config.id,
            )?.value || '';
        }

        this.dynamicFormValuesForReactiveForm.push({
          tempLabel: config.tempLabel,
          value: value,
          validators: validators,
        });
      },
    );

    this.FormEmbeded.emit({
      values: this.dynamicFormValuesForReactiveForm,
      dynamicForm: this.selectedDynamicForm,
      prefixLabel: this.prefixLabel,
    });
  }

  handleValidationInput(
    event: Event,
    config: DynamicFormConfigurationView | undefined,
  ): void {
    if (!config || !config.tempLabel) {
      return;
    }

    const inputElement = event.target as HTMLTextAreaElement;
    const value = inputElement.value;

    const maxLength = config.characterValidation ?? Infinity;

    const control = this.form.get(config.tempLabel);

    if (control && value.length > maxLength) {
      control.setValue(value.slice(0, maxLength));
    }
  }

  onMultipleTypeSelectionChange(e: Event) {
    const target = e.target as HTMLSelectElement | null;
    this.onMultipleTypeSelectionChangeWithValue(target?.value ?? '');
  }

  onMultipleTypeSelectionChangeWithValue(value: any) {
    if (value != '')
      this.selectedDynamicFormConfigurations =
        this.selectedDynamicForm.dynamicFormMultipleTypeConfigurations.find(
          (x: any) => x.id == value,
        ).dynamicFormConfigurations;

    this.selectedDynamicFormConfigurations?.sort(
      (a: DynamicFormConfigurationView, b: DynamicFormConfigurationView) => {
        return a.order - b.order;
      },
    );

    this.dynamicFormValuesForReactiveForm.push({
      tempLabel: this.prefixLabel + 'Type',
      value: value,
      validators: [],
    });
    this.tempLabelConfig(this.selectedDynamicFormConfigurations, this);
  }

  isRequired(controlName: string): boolean {
    const control = this.form.get(controlName);
    return control && control.hasValidator(Validators.required);
  }

  sortedOptions(config: any) {
    return (
      config.dynamicFormConfigurationOptions?.sort((a: any, b: any) =>
        this.alphaNumericCompareForSorting(String(a.option), String(b.option)),
      ) || []
    );
  }

  alphaNumericCompareForSorting(a: any, b: any) {
    a = a ?? '';
    b = b ?? '';

    const aHasLetters = /[a-zA-Z]/.test(a);
    const bHasLetters = /[a-zA-Z]/.test(b);

    if (aHasLetters && bHasLetters) {
      const aNumeric = parseFloat(a);
      const bNumeric = parseFloat(b);
      if (!isNaN(aNumeric) && !isNaN(bNumeric)) {
        if (aNumeric !== bNumeric) {
          return aNumeric - bNumeric;
        }
      }

      return a.localeCompare(b, undefined, { sensitivity: 'case' });
    } else if (aHasLetters) {
      return 1;
    } else if (bHasLetters) {
      return -1;
    } else {
      const aNumeric = parseFloat(a);
      const bNumeric = parseFloat(b);
      if (!isNaN(aNumeric) && !isNaN(bNumeric)) {
        return aNumeric - bNumeric;
      }

      return a.localeCompare(b, undefined, { sensitivity: 'case' });
    }
  }
}
